/**
 * Inline SVG icons.
 *
 * Inline rather than an icon package: the set is small, the shapes are simple,
 * and a dependency would ship a few thousand glyphs to render eighteen. They
 * inherit `currentColor` and size from `--icon-size`, so a status colour on a
 * row colours its icon without a second prop.
 *
 * Every icon is `aria-hidden`. An icon that carries meaning on its own is a
 * bug in the surrounding component - the label beside it is what a screen
 * reader gets, and icon-only buttons carry an `aria-label`.
 */

interface IconProps {
  size?: number;
  className?: string;
}

function svg(path: React.ReactNode, { size = 18, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {path}
    </svg>
  );
}

export const IconHome = (p: IconProps = {}) =>
  svg(<><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" /></>, p);

export const IconFolder = (p: IconProps = {}) =>
  svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />, p);

export const IconPlusSquare = (p: IconProps = {}) =>
  svg(<><rect x="3.5" y="3.5" width="17" height="17" rx="3" /><path d="M12 8.5v7M8.5 12h7" /></>, p);

export const IconPlus = (p: IconProps = {}) =>
  svg(<path d="M12 5v14M5 12h14" />, p);

export const IconHelp = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.4" /><path d="M12 17h.01" /></>, p);

export const IconSettings = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="3" /><path d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3" /></>, p);

export const IconInfo = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.8h.01" /></>, p);

export const IconPerson = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" /></>, p);

export const IconFace = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="M9 10h.01M15 10h.01M8.8 14.5c1.9 1.6 4.5 1.6 6.4 0" /></>, p);

export const IconCamera = (p: IconProps = {}) =>
  svg(<><path d="M3 8.5a2 2 0 0 1 2-2h2.2l1.3-2h6.6l1.3 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><circle cx="12" cy="13" r="3.2" /></>, p);

export const IconVideo = (p: IconProps = {}) =>
  svg(<><rect x="3" y="6.5" width="12.5" height="11" rx="2" /><path d="m15.5 11 5.5-3v8l-5.5-3Z" /></>, p);

export const IconPhone = (p: IconProps = {}) =>
  svg(<><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18.5h2" /></>, p);

export const IconImage = (p: IconProps = {}) =>
  svg(<><rect x="3" y="4.5" width="18" height="15" rx="2" /><circle cx="8.5" cy="10" r="1.5" /><path d="m4 17 4.8-4.4 4 3.4 3-2.4L20 17" /></>, p);

export const IconLayers = (p: IconProps = {}) =>
  svg(<><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3.5 12.5 8.5 4.7 8.5-4.7" /><path d="m3.5 16.8 8.5 4.7 8.5-4.7" /></>, p);

export const IconCube = (p: IconProps = {}) =>
  svg(<><path d="M12 2.8 20.5 7v10L12 21.2 3.5 17V7L12 2.8Z" /><path d="M3.5 7 12 11.5 20.5 7M12 11.5v9.7" /></>, p);

export const IconGrid = (p: IconProps = {}) =>
  svg(<><circle cx="7" cy="7" r="1.1" fill="currentColor" stroke="none" /><circle cx="12" cy="7" r="1.1" fill="currentColor" stroke="none" /><circle cx="17" cy="7" r="1.1" fill="currentColor" stroke="none" /><circle cx="7" cy="12" r="1.1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" /><circle cx="17" cy="12" r="1.1" fill="currentColor" stroke="none" /><circle cx="7" cy="17" r="1.1" fill="currentColor" stroke="none" /><circle cx="12" cy="17" r="1.1" fill="currentColor" stroke="none" /><circle cx="17" cy="17" r="1.1" fill="currentColor" stroke="none" /></>, p);

export const IconFiles = (p: IconProps = {}) =>
  svg(<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5" /></>, p);

export const IconTarget = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.5" /></>, p);

export const IconChevronRight = (p: IconProps = {}) =>
  svg(<path d="m9.5 5.5 7 6.5-7 6.5" />, p);

export const IconChevronUp = (p: IconProps = {}) =>
  svg(<path d="m5.5 14.5 6.5-6 6.5 6" />, p);

export const IconChevronDown = (p: IconProps = {}) =>
  svg(<path d="m5.5 9.5 6.5 6 6.5-6" />, p);

export const IconAlert = (p: IconProps = {}) =>
  svg(<><path d="M12 3.8 21 19.5H3L12 3.8Z" /><path d="M12 10v4M12 17h.01" /></>, p);

export const IconCheck = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="m8 12.3 2.7 2.7L16 9.6" /></>, p);

export const IconClock = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5.3l3.3 2" /></>, p);

export const IconSearch = (p: IconProps = {}) =>
  svg(<><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></>, p);

export const IconMinus = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="9" /><path d="M8.2 12h7.6" /></>, p);

export const IconLock = (p: IconProps = {}) =>
  svg(<><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" /></>, p);

export const IconDownload = (p: IconProps = {}) =>
  svg(<><path d="M12 4v10.5M8 11l4 4 4-4" /><path d="M4.5 19.5h15" /></>, p);

export const IconEye = (p: IconProps = {}) =>
  svg(<><path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></>, p);
export const IconEyeOff = (p: IconProps = {}) =>
  svg(<><path d="M4 4.5 20 20.5" /><path d="M9.6 6.2A9.4 9.4 0 0 1 12 5.8c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-3.2 3.9" /><path d="M6.6 8.1A17 17 0 0 0 2.5 12S6 18.2 12 18.2a9.6 9.6 0 0 0 3.2-.55" /><path d="M10.1 10.2a3 3 0 0 0 4.05 4.2" /></>, p);

/**
 * The SentyCap mark.
 *
 * The real brand asset, not a redrawing of it. Earlier attempts to reproduce
 * the letterforms as SVG paths were close and wrong in the way hand-traced
 * logos always are - the folds, the gradient stops and the C's opening all
 * drifted. The artwork is exported once from the approved brand sheet with a
 * transparent ground and used everywhere at whatever size is asked for.
 *
 * `aria-hidden`: the wordmark beside it carries the name, so a screen reader
 * that announced this too would say "SentyCap" twice.
 */
import markUrl from "@/assets/sentycap-mark.png";

export function BrandMark({ size = 30 }: { size?: number; id?: string }) {
  return (
    <img
      src={markUrl}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      className="brand-mark"
      draggable={false}
    />
  );
}

/**
 * The wordmark. Two tones, as the brand sheet has them: the product half in the
 * foreground colour, the capture half in brand blue.
 */
export function BrandWordmark() {
  return (
    <span className="wordmark">
      <span className="wordmark__a">Senty</span>
      <span className="wordmark__b">Cap</span>
    </span>
  );
}
